import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import OutputIcon from "@mui/icons-material/Output";
import InputIcon from "@mui/icons-material/Input";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

const DECIMAL_PATTERN = /^\d*(\.\d{0,2})?$/;

const money = (value) => {
  const n = Number(value || 0);
  if (Number.isNaN(n)) return "$0.00";
  return `$${n.toFixed(2)}`;
};

const parseAmount = (raw) => {
  if (raw === "" || !DECIMAL_PATTERN.test(raw)) return null;
  const n = Number(raw);
  if (Number.isNaN(n)) return null;
  return n;
};

export default function RegisterCashOutDialog({
  open,
  onClose,
  onConfirm,
  currentCashAvailable = 0,
  loading = false,
  modalZIndex,
  locationName,
}) {
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payInAmount, setPayInAmount] = useState("");
  const [error, setError] = useState("");
  const isDark = useTheme().palette.mode === "dark";
  const fieldBg = isDark ? "#141416" : "#fafafa";
  const ink = isDark ? "#f2f2f2" : "#37474f";

  useEffect(() => {
    if (!open) return;
    setPayoutAmount("");
    setPayInAmount("");
    setError("");
  }, [open, currentCashAvailable]);

  const payoutValue = parseAmount(payoutAmount);
  const payInValue = parseAmount(payInAmount);
  const payoutActive = payoutValue != null && payoutValue > 0;
  const payInActive = payInValue != null && payInValue > 0;

  const cashRemaining = useMemo(() => {
    if (payoutActive) return +(currentCashAvailable - payoutValue).toFixed(2);
    if (payInActive) return +(currentCashAvailable + payInValue).toFixed(2);
    return currentCashAvailable;
  }, [payoutActive, payInActive, payoutValue, payInValue, currentCashAvailable]);

  const handleAmountChange = (field, raw) => {
    if (raw !== "" && !DECIMAL_PATTERN.test(raw)) return;
    if (field === "payout") {
      setPayoutAmount(raw);
      if (raw !== "") setPayInAmount("");
    } else {
      setPayInAmount(raw);
      if (raw !== "") setPayoutAmount("");
    }
    if (error) setError("");
  };

  const handleConfirm = () => {
    if (loading) return;
    if (payoutActive) {
      if (payoutValue > currentCashAvailable) {
        setError(
          `Cannot exceed current cash available (${money(currentCashAvailable)})`
        );
        return;
      }
      onConfirm({ type: "payout", amount: +payoutValue.toFixed(2) });
      return;
    }
    if (payInActive) {
      onConfirm({ type: "pay_in", amount: +payInValue.toFixed(2) });
      return;
    }
    setError("Enter a payout or pay in amount");
  };

  const remainingNegative = cashRemaining < 0;
  const exceedsAvailable = payoutActive && payoutValue > currentCashAvailable;
  const payoutError = exceedsAvailable
    ? `Cannot exceed current cash available (${money(currentCashAvailable)})`
    : payoutActive
      ? error
      : "";
  const payInError = payInActive ? error : "";
  const formError = !payoutActive && !payInActive ? error : "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={modalZIndex != null ? { zIndex: modalZIndex } : undefined}
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <Box
        sx={{
          background: isDark
            ? "linear-gradient(135deg, #141416 0%, #1b1b1e 100%)"
            : "linear-gradient(135deg, #37474f 0%, #546e7a 100%)",
          color: "#fff",
          px: 3,
          py: 3,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
        }}
      >
        <OutputIcon sx={{ fontSize: 28 }} />
        <div>
          <Typography component="div" sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
            Opening{locationName ? ` · ${locationName}` : ""}
          </Typography>
          <Typography component="div" sx={{ opacity: 0.9, fontSize: "0.8rem" }}>
            Record a payout or pay in
          </Typography>
        </div>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Stack spacing={2}>
          <Box
            sx={{
              border: "1px solid rgba(46,125,50,0.35)",
              borderRadius: 2,
              p: 1.25,
              bgcolor: "rgba(46,125,50,0.08)",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <AccountBalanceWalletIcon sx={{ color: "#2e7d32", fontSize: 20 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Current cash available
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: "#2e7d32" }}>
                {money(currentCashAvailable)}
              </Typography>
            </Box>
          </Box>

          <TextField
            label="Payout"
            value={payoutAmount}
            onChange={(e) => handleAmountChange("payout", e.target.value)}
            error={Boolean(payoutError)}
            helperText={payoutError || "Cash removed from the drawer."}
            fullWidth
            size="small"
            disabled={loading}
            inputProps={{ inputMode: "decimal" }}
            placeholder="0.00"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AttachMoneyIcon sx={{ fontSize: 20, color: ink }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: fieldBg,
                "&.Mui-focused fieldset": { borderColor: isDark ? "#c5a06f" : "#37474f" },
              },
            }}
          />

          <TextField
            label="Pay in"
            value={payInAmount}
            onChange={(e) => handleAmountChange("pay_in", e.target.value)}
            error={Boolean(payInError)}
            helperText={payInError || "Cash added to the drawer."}
            fullWidth
            size="small"
            disabled={loading}
            inputProps={{ inputMode: "decimal" }}
            placeholder="0.00"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AttachMoneyIcon sx={{ fontSize: 20, color: "#2e7d32" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: fieldBg,
                "&.Mui-focused fieldset": { borderColor: "#2e7d32" },
              },
            }}
          />

          {formError ? (
            <Typography variant="caption" color="error">
              {formError}
            </Typography>
          ) : null}

          <Box
            sx={{
              border: "1px dashed",
              borderColor: remainingNegative
                ? "#ef9a9a"
                : isDark
                  ? "rgba(255,255,255,0.28)"
                  : "#bdbdbd",
              borderRadius: 2,
              p: 1.25,
              bgcolor: remainingNegative
                ? isDark
                  ? "rgba(198,40,40,0.16)"
                  : "#ffebee"
                : fieldBg,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Cash remaining
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                color: remainingNegative ? "#c62828" : ink,
              }}
            >
              {money(cashRemaining)}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: isDark ? "rgba(255,255,255,0.12)" : "#e0e0e0",
          bgcolor: fieldBg,
        }}
      >
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          startIcon={
            loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : payInActive && !payoutActive ? (
              <InputIcon />
            ) : (
              <OutputIcon />
            )
          }
          disabled={loading || remainingNegative}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          {loading ? "Recording…" : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
